import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { GlobalContext } from "../components/GlobalContext";
import {
  useConnectionState,
  useDataChannel,
  useLocalParticipant,
  useRoomContext,
} from "@livekit/components-react";
import { LocalStorageKeys, UserState } from "../types";
import { TeamsFxContext } from "../components/teams/TeamsFxContext";
import { useAxios } from "./axios";
import { LocalParticipant, RemoteParticipant } from "livekit-client";
import { useRoom } from "../components/context/room/room";
import { useLogEvent } from "./log";
import * as Sentry from "@sentry/react";
import amplitude from "../amplitude";

const useUser = () => {
  const context = useContext(GlobalContext);
  return [context.user, context.setUser] as const;
};

const baseUrl = import.meta.env.VITE_API_GATEWAY_URL ?? "http://localhost:8740";

export const useTeamsLogin = () => {
  const [loading, setLoading] = useState(false);
  const teamsUserCredential = useContext(TeamsFxContext).teamsUserCredential;
  const axios = useAxios();
  const [, setUser] = useUser();
  const logEvent = useLogEvent();

  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const login = useCallback(async () => {
    logEvent({ type: "attempt_login_with_teams" });
    let retryCount = 0;
    let loggedIn = false;
    const maxRetries = 5;
    while (retryCount < maxRetries && !loggedIn) {
      try {
        setLoading(true);
        await delay(500 * retryCount);

        const url = new URL(`/v1/auth/login`, baseUrl);
        const response = await axios.post(url.toString());

        // TODO: Use login hook
        const { user } = response.data;
        // as {
        //   user: User;
        // };
        setUser((prevState) => ({
          ...prevState,
          auth: user,
        }));

        Sentry.setUser({
          id: response.data.user.id,
          email: response.data.user.email,
          username: response.data.user.name,
        });
        amplitude?.setUserId(response.data.user.id);

        localStorage.setItem(LocalStorageKeys.EXPECT_LOGIN, "true");
        loggedIn = true;
        logEvent({ type: "succeed_login_with_teams" });
      } catch (error) {
        // Retry with consent screen if fails first time
        if (retryCount === 0) {
          await teamsUserCredential?.login(["User.Read"]);
        }
        if (retryCount >= maxRetries) {
          logEvent({
            type: "fail_login_with_teams",
            details: { error: error },
          });
          throw error;
        }
      } finally {
        retryCount++;
        setLoading(false);
      }
    }
    setLoading(false);
  }, [setLoading, teamsUserCredential, axios, setUser, logEvent]);

  // Login user automatically with expected login
  useEffect(() => {
    const expectLogin = localStorage.getItem(LocalStorageKeys.EXPECT_LOGIN);
    if (expectLogin) {
      login();
    }
  }, [login]);

  return { login, loading };
};

// Language to render captions with (if autodetect enabled, use detected language)
export const useRenderLanguage = () => {
  const [user] = useUser();
  const { room } = useRoom();
  return useMemo(() => {
    if (user.metadata.selectedLanguage === "autodetect") {
      if (user.metadata.detectedLanguage) {
        return user.metadata.detectedLanguage;
      }
      if (room) {
        // Default to an english dialect if exists in room, else choose first
        return (
          room.languages.find((l) => l.includes("en")) ?? room.languages[0]
        );
      }
      // Fallback to english if not in room and language not detected yet
      return "en-US";
    }
    return user.metadata.selectedLanguage;
  }, [user.metadata.selectedLanguage, user.metadata.detectedLanguage, room]);
};

const updateMetadataWithRetry = async (
  localParticipant: LocalParticipant,
  metadata: string
) => {
  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  let attempts = 0;
  const maxAttempts = 3;
  const delay = 500; // 500ms delay between attempts

  while (attempts < maxAttempts) {
    try {
      await localParticipant.setMetadata(metadata);
      // Success, exit the function
      return;
    } catch (error) {
      attempts++;
      console.warn(
        `Error updating metadata (attempt ${attempts})`,
        JSON.stringify(error)
      );
      if (attempts >= maxAttempts) {
        console.error("Max retries reached, failed to update metadata");
        throw error;
      }
      await sleep(delay);
    }
  }
};

const decoder = new TextDecoder();
export const useUserMetadata = () => {
  const [user, setUser] = useUser();

  const connectionState = useConnectionState();
  const { localParticipant } = useLocalParticipant();

  useDataChannel("autodetect_source_language", (message) => {
    const event = JSON.parse(decoder.decode(message.payload)) as {
      participant_id: string;
      language: string;
    };
    if (event.participant_id === localParticipant.sid) {
      console.log("set detected language", event.language);
      setUser((prevState) => ({
        ...prevState,
        metadata: { ...prevState.metadata, detectedLanguage: event.language },
      }));
    }
  });

  // Store some metadata settings to local storage
  useEffect(() => {
    localStorage.setItem(
      LocalStorageKeys.USER_METADATA_LANGUAGE,
      user.metadata.selectedLanguage
    );
  }, [user.metadata]);

  // Remote updates to local participant metadata
  const livekitRoom = useRoomContext();
  useEffect(() => {
    const onParticipantMetadataChanged = (
      _: string | undefined,
      participant: RemoteParticipant | LocalParticipant
    ) => {
      if (!participant.isLocal) {
        return;
      }

      if (!participant.metadata) {
        return;
      }

      const metadata = JSON.parse(participant.metadata) as {
        language: string;
        voice: UserState["metadata"]["voice"];
      };

      setUser((prevState) => ({
        ...prevState,
        metadata: {
          ...prevState.metadata,
          selectedLanguage: metadata.language,
          voice: metadata.voice,
        },
      }));
    };

    livekitRoom.on("participantMetadataChanged", onParticipantMetadataChanged);

    return () => {
      livekitRoom.off(
        "participantMetadataChanged",
        onParticipantMetadataChanged
      );
    };
  }, [livekitRoom, setUser]);

  useEffect(() => {
    if (connectionState === "connected") {
      const newMetadata = JSON.stringify({
        name: user.metadata.name ?? "Guest",
        participant_type: user.metadata.participantType,
        language: user.metadata.selectedLanguage,
        detected_language: user.metadata.detectedLanguage,
        voice: user.metadata.voice,
        ai_voice_gender: user.metadata.aiVoiceGender,
        ai_voice_short_name: user.metadata.aiVoice,
        bot_participant_id: user.metadata.botParticipantId,
        org_id: user.auth?.org_id,
        timezone: user.metadata.timezone,
      });

      // Prevent unnecessary updates by checking if metadata is actually changed
      if (localParticipant.metadata !== newMetadata) {
        updateMetadataWithRetry(localParticipant, newMetadata);
      }
    }
  }, [connectionState, localParticipant, user.metadata, user.auth]);
};

export default useUser;
